import { forwardRef, useLayoutEffect, useState } from 'react';
import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { Badge } from '../../Badge';
import { Box } from '../../Box';
import tabRootDefaultStyle from './tabRootDefaultStyle';
import tabContentDefaultStyle from './tabContentDefaultStyle';
import tabDefaultStyle from './tabDefaultStyle';
import tabIndicatorDefaultStyle from './tabIndicatorDefaultStyle';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { TabScrollButton } from './TabScrollButton';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const findActiveTabValue = tabs => {
  const index = tabs.findIndex(t => t.active);
  // if -1 it wasn't found so set the active to the value of tab 0 or index of 0
  return index === -1 ? tabs[0].value || 0 : tabs[index].value || index;
};

/** Compact elements that show attributes or action  */
export const Tabs = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    tabs = [],
    onActivate,
    ariaLabel = 'Tabs where each tab needs to be selected manually',
    id,
    sx,
    ...rest
  } = _ref;
  // This has the value or the index of the tab.  If there is a value we use the value otherwise we use the index for the value
  const [activeTabValue, setActiveTabValue] = useState(tabs[0]?.value ? tabs[0].value : 0);
  useLayoutEffect(() => {
    const foundTab = findActiveTabValue(tabs);
    setActiveTabValue(foundTab);
  }, [tabs]);
  const theme = useInterstateTheme();
  const handleChange = (event, newValue) => {
    if (onActivate) {
      onActivate(newValue);
    }
    setActiveTabValue(newValue);
  };
  const effectiveId = id || 'interstate-tabs';
  const effectiveDataTestId = rest['data-testid'] || 'interstate-tabs-data-testid';
  const _tabsStyle = mergeSxStyle(tabRootDefaultStyle(theme), sx);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MuiTabs, {
      value: activeTabValue,
      onChange: handleChange,
      TabIndicatorProps: {
        style: tabIndicatorDefaultStyle(theme)
      },
      ref: ref,
      id: effectiveId,
      "data-testid": effectiveDataTestId,
      ScrollButtonComponent: TabScrollButton,
      scrollButtons: "auto",
      allowScrollButtonsMobile: true,
      variant: "scrollable",
      sx: _tabsStyle,
      "aria-label": ariaLabel,
      children: tabs?.filter(tab => {
        return tab.display !== false;
      }).map((tab, index) => {
        const {
          badge,
          badgeChildren,
          disabled,
          label,
          icon,
          id,
          sx,
          value,
          href,
          target,
          rel,
          ...rest
        } = tab;
        const effectiveValue = value || index;
        const isCurrentTab = effectiveValue === activeTabValue;
        const _tabStyle = mergeSxStyle(tabDefaultStyle(theme, isCurrentTab), sx);
        return /*#__PURE__*/_jsx(MuiTab
        // eslint-disable-next-line react/no-array-index-key
        , {
          value: effectiveValue,
          label: label,
          disabled: disabled,
          disableFocusRipple: true,
          disableRipple: true,
          component: href ? 'a' : 'button',
          href: href,
          target: target,
          rel: rel || target === '_blank' ? 'noopener noreferrer' : rel,
          icon: badge &&
          /*#__PURE__*/
          // eslint-disable-next-line react/no-children-prop
          _jsx(Badge, {
            variant: "light",
            children: badgeChildren
          }) || icon,
          iconPosition: badge && 'end' || 'start',
          sx: _tabStyle,
          id: `${effectiveId}-tab-id-${index}`,
          "aria-controls": `${effectiveId}-tabs-id-${index}`,
          "data-testid": rest['data-testid'] || `${effectiveDataTestId}-tabs-data-testid-${index}`
        }, `key-${index}`);
      })
    }), tabs?.filter(tab => {
      return tab.display !== false && tab.component;
    }).map((tab, index) => {
      const {
        component,
        value
      } = tab;
      const effectiveValue = value || index;
      return /*#__PURE__*/_jsx(Box, {
        role: "tabpanel",
        hidden: activeTabValue !== effectiveValue,
        sx: tabContentDefaultStyle(theme),
        id: `${effectiveId}-tabs-id-${index}`,
        "data-testid": `${effectiveDataTestId}-tab-data-testid-${index}`,
        "aria-labelledby": `${effectiveId}-tab-id-${index}`,
        children: component
      }, effectiveValue);
    })]
  });
});
Tabs.displayName = 'Tabs';